import { Button, Grid, Typography } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import jsQR from 'jsqr';
import React, { useEffect, useRef, useState } from 'react';

// Decode buffer edge, in pixels. The crop taken from the camera scales with the
// stream, but it is downscaled into a buffer this size before jsQR sees it, so a
// 1080p camera costs the same per frame as a 480p one. Comfortably above the
// ~150px a QR code needs to decode reliably.
const MAX_DECODE_SIZE = 512;

// Edge of the scanned square as a fraction of the visible preview. The preview is
// cropped to a square and this is drawn as the guide brackets, so the region the
// user aims at and the region actually decoded are the same thing. Keeping it well
// under the full frame matters on a printed label sheet, where several QR codes sit
// close together and a generous scan area would grab whichever one it saw first.
const SCAN_FRACTION = 0.5;

// How often to attempt a decode. The old one-second gap meant holding the phone
// still in exactly the right spot for a full second per attempt, which reads as
// "it isn't scanning" long before anyone suspects the camera.
const SCAN_INTERVAL_MS = 250;

// Remembered camera choice. Phones expose several rear cameras and the one the OS
// picks by default is often the ultra-wide, which on many Android handsets is
// fixed-focus — so filling the frame with a label means getting closer than it can
// focus. Letting the user pick the main lens once and keeping that choice is the
// only reliable fix; the labels are too vendor-specific to guess from.
const CAMERA_PREF_KEY = 'qrScannerDeviceId';

const readSavedDeviceId = () => {
  try {
    return window.localStorage.getItem(CAMERA_PREF_KEY) || undefined;
  } catch (err) {
    return undefined;
  }
};

const saveDeviceId = (deviceId) => {
  try {
    if (deviceId) {
      window.localStorage.setItem(CAMERA_PREF_KEY, deviceId);
    } else {
      window.localStorage.removeItem(CAMERA_PREF_KEY);
    }
  } catch (err) {
    // Private browsing and blocked storage are fine — the choice just won't stick.
  }
};

// Rear cameras first, so cycling reaches a usable lens before the selfie camera.
const backCamerasFirst = (devices) => {
  const isBack = (device) => /back|rear|environment/i.test(device.label || '');

  return [...devices.filter(isBack), ...devices.filter((d) => !isBack(d))];
};

export default function Scanner(props) {
  const [isCameraAllowed, setIsCameraAllowed] = useState(-1);
  const video = useRef();
  const interval = useRef(null);
  const canvas = useRef();
  const isUnmounted = useRef(false);
  // Edge of the square cropped out of the camera frame, in source pixels.
  const scanRegion = useRef(0);
  // Where to draw the tap-to-focus ring, in CSS pixels inside the video box.
  const [focusRing, setFocusRing] = useState(null);
  const focusRingTimeout = useRef(null);
  // Cameras offered by the device, and which one is live. Only discoverable once a
  // stream is running: enumerateDevices returns blank labels until then.
  const [cameras, setCameras] = useState([]);
  const [activeDeviceId, setActiveDeviceId] = useState(() => readSavedDeviceId());

  const getImageDataFromCanvas = () => {
    if (
      isCameraAllowed !== 1 ||
      canvas.current.width === 0 ||
      canvas.current.height === 0
    )
      return;

    const context = canvas.current.getContext('2d', {
      willReadFrequently: true,
    });

    if (!context) return;

    // Crop the centre square at full camera resolution, then let drawImage
    // downscale it into the fixed-size decode buffer.
    const source = scanRegion.current;
    const sx = (video.current.videoWidth - source) / 2;
    const sy = (video.current.videoHeight - source) / 2;
    context.drawImage(
      video.current,
      sx,
      sy,
      source,
      source,
      0,
      0,
      canvas.current.width,
      canvas.current.height
    );
    const data = context.getImageData(
      0,
      0,
      canvas.current.width,
      canvas.current.height
    );
    const code = jsQR(data.data, data.width, data.height, {
      inversionAttempts: 'dontInvert',
    });

    if (code?.data) {
      // Two modes. Without onScan the scanner navigates, which is what the landing
      // page wants. With onScan the caller handles the decoded URL itself — the
      // sample checkout dialog adds the product to its cart and stays put, because
      // navigating away would throw out the customer already captured in the form.
      // The camera is released either way; only the closing differs.
      if (props.onScan) {
        releaseCamera();
        props.onScan(code.data);
        return;
      }

      stopStreamAndClose();
      window.location.href = code?.data;
    }
  };

  // Releases the camera without telling the parent to close. Split out so onScan
  // callers can keep their dialog open on the next step.
  const releaseCamera = () => {
    clearInterval(interval?.current);

    if (!video.current.srcObject) return false;

    video.current.srcObject.getTracks().forEach((t) => {
      t.stop();
    });

    video.current.pause();
    video.current.srcObject = null;
    return true;
  };

  const stopStream = () => {
    if (!releaseCamera()) return;

    props.handleOnClose();
  };

  const stopStreamAndClose = () => {
    releaseCamera();
    props.handleOnClose();
  };

  const startStream = (deviceId) => {
    if (!('mediaDevices' in navigator) || !navigator.mediaDevices?.getUserMedia) {
      setIsCameraAllowed(0);
      return;
    }

    // Ask for the rear camera by facing mode and let the OS pick the lens.
    //
    // This used to enumerate devices and select the *last* one whose label said
    // "back", which on a multi-lens phone is typically the telephoto — a heavily
    // zoomed view that cannot focus on a QR code at arm's length. Device labels
    // are only populated after camera permission has been granted, so the first
    // scan of a page session saw no labels and fell through to the sensible
    // default, while later scans in that same session picked the zoomed lens.
    // Nothing caught it before because a successful scan used to navigate away,
    // so there was never a second scan in one page session.
    // Resolution matters more than it looks. With no constraint the browser hands
    // back its default — usually 640x480 — which leaves so few pixels on a label
    // that the code only decodes when the phone is held right up against it, and
    // that is nearer than a phone's main lens can focus. Asking for 1080p lets the
    // user stand back far enough for autofocus to lock while still giving jsQR
    // plenty of detail. Both values are `ideal`, so a camera that cannot manage it
    // degrades instead of failing.
    // An explicit choice wins; otherwise let the OS pick the rear camera.
    const videoConstraints = deviceId
      ? { deviceId: { exact: deviceId } }
      : { facingMode: { ideal: 'environment' } };

    navigator.mediaDevices
      .getUserMedia({
        video: {
          ...videoConstraints,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      })
      .then((stream) => {
        // Permission can still be pending when the dialog is closed. If the
        // scanner is gone by the time the stream arrives, stop it here — the
        // unmount cleanup has already run and would never see this one.
        if (isUnmounted.current || !video.current) {
          stream.getTracks().forEach((t) => {
            t.stop();
          });
          return;
        }

        video.current.srcObject = stream;
        requestContinuousFocus(stream);
        refreshCameraList(stream);
      })
      .catch(() => {
        // A remembered camera can disappear — a different phone, cleared
        // permissions, an external webcam unplugged. Forget it and take whatever
        // the OS offers rather than leaving the scanner dead.
        if (deviceId) {
          saveDeviceId(undefined);
          setActiveDeviceId(undefined);
          startStream(undefined);
          return;
        }

        setIsCameraAllowed(0);
      });
  };

  // Labels are only populated once a stream is live, which is why this runs after
  // getUserMedia rather than before it.
  const refreshCameraList = (stream) => {
    const [track] = stream.getVideoTracks();
    const liveDeviceId = track?.getSettings?.().deviceId;

    if (liveDeviceId) setActiveDeviceId(liveDeviceId);

    if (!navigator.mediaDevices?.enumerateDevices) return;

    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        if (isUnmounted.current) return;

        setCameras(
          backCamerasFirst(devices.filter((d) => d.kind === 'videoinput'))
        );
      })
      .catch(() => {});
  };

  const switchCamera = () => {
    if (cameras.length < 2) return;

    const current = cameras.findIndex((c) => c.deviceId === activeDeviceId);
    const next = cameras[(current + 1) % cameras.length];

    saveDeviceId(next.deviceId);
    setActiveDeviceId(next.deviceId);
    releaseCamera();
    setIsCameraAllowed(-1);
    startStream(next.deviceId);
  };

  // Ask the camera to keep refocusing rather than lock focus once at stream start.
  // Chrome on Android honours this; iOS Safari does not expose focusMode at all, so
  // there it is a no-op and the resolution bump above is what buys the working
  // distance. Entirely best-effort — a camera that refuses should still scan.
  const requestContinuousFocus = (stream) => {
    const [track] = stream.getVideoTracks();

    if (!track?.getCapabilities) return;

    let capabilities;

    try {
      capabilities = track.getCapabilities();
    } catch (err) {
      return;
    }

    if (!capabilities?.focusMode?.includes('continuous')) return;

    track
      .applyConstraints({ advanced: [{ focusMode: 'continuous' }] })
      .catch(() => {});
  };

  useEffect(() => {
    if (!props.isOpen) stopStream();

    if (props.isOpen && isCameraAllowed !== 0 && !video.current.srcObject)
      startStream(activeDeviceId);
  }, [props.isOpen]);

  useEffect(() => {
    if (props.isOpen && isCameraAllowed === 0) return;

    interval.current = setInterval(() => {
      getImageDataFromCanvas();
    }, SCAN_INTERVAL_MS);

    return () => {
      clearInterval(interval?.current);
    };
  }, [isCameraAllowed]);

  // The camera must not outlive the component. Callers that unmount the scanner
  // outright — rather than toggling isOpen — would otherwise leave the device's
  // recording indicator on.
  useEffect(() => {
    // Captured on mount: the <video> element is rendered unconditionally, so this
    // is the same node the cleanup needs, and reading the ref during teardown
    // would be too late.
    const videoElement = video.current;
    isUnmounted.current = false;

    return () => {
      isUnmounted.current = true;
      clearInterval(interval?.current);
      window.clearTimeout(focusRingTimeout.current);

      videoElement?.srcObject?.getTracks().forEach((t) => {
        t.stop();
      });
    };
  }, []);

  const getVideoTrack = () => {
    const tracks = video.current?.srcObject?.getVideoTracks?.();

    return tracks?.length ? tracks[0] : undefined;
  };

  // Restarting the stream makes the camera run a fresh autofocus sweep at whatever
  // distance the phone is now being held. It is the only lever available on iOS
  // Safari, which exposes no focus controls at all — there is no web API for
  // tap-to-focus there, so this stands in for it.
  const restartStream = () => {
    releaseCamera();
    // Drops back to the "starting up" state so the decode interval, which is keyed
    // on this, is torn down and re-armed once the new stream reports in. Without it
    // releaseCamera's clearInterval would leave scanning dead after a tap.
    setIsCameraAllowed(-1);
    startStream(activeDeviceId);
  };

  // Tap-to-focus. Chrome on Android can aim the camera at a point via
  // pointsOfInterest; everything else falls back to the restart above.
  const focusAtPoint = (x, y) => {
    const track = getVideoTrack();

    if (!track?.getCapabilities) {
      restartStream();
      return;
    }

    let capabilities;

    try {
      capabilities = track.getCapabilities();
    } catch (err) {
      capabilities = undefined;
    }

    const constraint = {};

    if (capabilities?.pointsOfInterest) {
      constraint.pointsOfInterest = [{ x, y }];
    }

    if (capabilities?.focusMode?.includes('single-shot')) {
      constraint.focusMode = 'single-shot';
    } else if (capabilities?.focusMode?.includes('continuous')) {
      constraint.focusMode = 'continuous';
    }

    if (Object.keys(constraint).length === 0) {
      restartStream();
      return;
    }

    track
      .applyConstraints({ advanced: [constraint] })
      .catch(() => {
        restartStream();
      });
  };

  const handleVideoTap = (event) => {
    if (isCameraAllowed !== 1) return;

    const rect = event.currentTarget.getBoundingClientRect();

    if (!rect.width || !rect.height) return;

    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;

    // Ring is positioned in CSS pixels within the video box, not normalised.
    setFocusRing({ left: event.clientX - rect.left, top: event.clientY - rect.top });
    window.clearTimeout(focusRingTimeout.current);
    focusRingTimeout.current = window.setTimeout(() => setFocusRing(null), 900);

    focusAtPoint(Math.min(Math.max(x, 0), 1), Math.min(Math.max(y, 0), 1));
  };

  const handleOnVideoLoad = () => {
    const { videoWidth, videoHeight } = video.current;

    // The preview crops the camera to a centre square, so the shorter side is what
    // the user can actually see. The brackets mark SCAN_FRACTION of that square and
    // this is the matching source region — what's inside the guides is exactly what
    // gets decoded.
    const source = Math.floor(
      Math.min(videoWidth, videoHeight) * SCAN_FRACTION
    );

    scanRegion.current = source;
    canvas.current.width = canvas.current.height = Math.min(
      source,
      MAX_DECODE_SIZE
    );
    setIsCameraAllowed(1);
  };

  const handleVideoFeedStopped = () => {
    setIsCameraAllowed(0);
  };

  useEffect(() => {
    video.current.srcObject?.getTracks().forEach((t) => {
      t.addEventListener('ended', handleVideoFeedStopped);
    });

    return () => {
      video.current.srcObject?.getTracks().forEach((t) => {
        t.removeEventListener('ended', handleVideoFeedStopped);
      });
    };
  });

  const showVideoFeed = props.isOpen && isCameraAllowed === 1;

  // 1-based position of the live camera. Vendor labels ("camera2 0, facing back")
  // are no use to a salesperson, so the count is the only honest thing to show —
  // the preview tells them whether they've landed on the right one.
  const activeCameraPosition = () => {
    const index = cameras.findIndex((c) => c.deviceId === activeDeviceId);

    return index === -1 ? 1 : index + 1;
  };

  const cameraAwaiting =
    "To use the QR scanner, you will need to allow access to your device's camera. If you have not already allowed this access, you will be prompted each time you open the scanner.";
  const cameraDenied =
    "To use the QR scanner, you will need to allow access to your device's camera. If you have denied this access previously and want to use this function, you will need to change your browser settings to allow camera access and reload the page.";

  return (
    <div>
      {/* Callers that already provide their own title bar pass hideHeader, so the
          scanner doesn't stack a second heading and a second close button on top
          of theirs. */}
      {props.hideHeader ? undefined : (
        <Grid
          container
          style={{
            width: '100%',
            color: '#28C4FC',
            backgroundColor: '#142E3E',
            borderRadius: '5px 5px 0px 0px',
          }}
        >
          <Grid item xs={8} sm={8} md={8} lg={9}>
            <Typography variant="h5" style={{ margin: '15px' }}>
              QR Scanner
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={4} lg={3} style={{ textAlign: 'right' }}>
            <Button
              variant="contained"
              style={{ margin: '12px 15px' }}
              onClick={() => stopStreamAndClose()}
            >
              <Close></Close>
            </Button>
          </Grid>
        </Grid>
      )}
      <div style={{ position: 'relative' }}>
        {!showVideoFeed ? (
          <Typography variant="subtitle1" style={{ margin: '20px 15px' }}>
            {isCameraAllowed === -1 ? cameraAwaiting : cameraDenied}
          </Typography>
        ) : undefined}
        {/* The preview is cropped to a centre square rather than showing the whole
            frame. A portrait 1080x1920 stream at full width renders about 1500px
            tall, which pushed the camera controls below the fold — and all of that
            extra picture was outside the scanned region anyway. paddingTop rather
            than aspect-ratio so it holds up on older mobile browsers. */}
        <div
          onClick={handleVideoTap}
          style={{
            display: showVideoFeed ? 'block' : 'none',
            position: 'relative',
            width: '100%',
            maxWidth: 360,
            paddingTop: 'min(100%, 360px)',
            margin: '0 auto',
            overflow: 'hidden',
            backgroundColor: '#000000',
            cursor: 'pointer',
          }}
        >
          <video
            ref={video}
            autoPlay={true}
            muted={true}
            playsInline={true}
            onLoadedMetadata={() => handleOnVideoLoad()}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              display: 'block',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          ></video>

          {focusRing ? (
            <span
              style={{
                position: 'absolute',
                left: focusRing.left,
                top: focusRing.top,
                width: 64,
                height: 64,
                marginLeft: -32,
                marginTop: -32,
                border: '2px solid #28C4FC',
                borderRadius: '50%',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.35)',
                pointerEvents: 'none',
              }}
            ></span>
          ) : undefined}

          {/* Guide brackets. Inside the video wrapper so they stay centred on the
              picture rather than on the picture plus the hint line below it. */}
          <span
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate3d(-50%, -50%, 0)',
              width: '50%',
              pointerEvents: 'none',
            }}
          >
          <svg viewBox="0 0 100 100" width="100%">
            <path
              d="M25,2 L2,2 L2,25"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M2,75 L2,98 L25,98"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M75,98 L98,98 L98,75"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M98,25 L98,2 L75,2"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          </span>
        </div>

        {showVideoFeed ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              padding: '10px 12px',
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="caption" style={{ color: '#6d7f89' }}>
              Tap the picture to focus
            </Typography>

            {cameras.length > 1 ? (
              <Button variant="outlined" size="small" onClick={switchCamera}>
                {`Switch camera ${activeCameraPosition()}/${cameras.length}`}
              </Button>
            ) : undefined}
          </div>
        ) : undefined}
        <canvas
          ref={canvas}
          style={{
            display: 'none',
          }}
        ></canvas>
      </div>
    </div>
  );
}
